import { Component, inject, OnInit } from '@angular/core';
import {
  NbAutocompleteModule,
  NbCheckboxModule,
  NbFormFieldModule,
  NbInputModule,
  NbListModule,
  NbOptionModule,
  NbToggleModule,
} from '@nebular/theme';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { IInvestmentWizard } from '../wizard-interface';
import { ReactiveFormsModule } from '@angular/forms';
import { RoadmapService } from '../../../../../@core/utils';
import { BehaviorSubject } from 'rxjs';
import { KPI } from '../../../../../@core/api';
import { InvestmentWizardService } from '../../../../../@core/utils/investment-wizard-service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-investment-impact',
  standalone: true,
  imports: [
    NbListModule,
    CommonModule,
    NbInputModule,
    NbFormFieldModule,
    TranslateModule,
    ReactiveFormsModule,
    NbToggleModule,
    NbAutocompleteModule,
    NbOptionModule,
    NbCheckboxModule,

  ],
  template: `
    <div class="d-flex flex-row">
      <ng-container *ngIf="impacts$ | async as impacts">
        <nb-list>
          @for (roadmapIndicator of rIndicators(); track roadmapIndicator.id) {
            <nb-list-item>
              <nb-checkbox (checkedChange)="checkedChange($event, roadmapIndicator)"
                           [checked]="selected(roadmapIndicator.id)">
                {{ roadmapIndicator.name }}
              </nb-checkbox>
            </nb-list-item>
          }
        </nb-list>
      </ng-container>
    </div>
  `,
  styleUrls: ['../wizard-index.component.scss'],
})
export class InvestmentImpactComponent implements OnInit, IInvestmentWizard {
  readonly stateKey = 'INVESTMENT_IMPACT_SELECTION';
  state = () => this.wizardService.getState(this.stateKey);

  // dependencies
  private roadmapService = inject(RoadmapService);
  private wizardService = inject(InvestmentWizardService);

  // data for template
  impacts$: BehaviorSubject<KPI.RoadmapIndicator[]>;
  rIndicators = toSignal(this.roadmapService.roadmapIndicators(), { initialValue: [] });

  ngOnInit(): void {
    let currentState = this.state(),
      energyState = this.wizardService.getState('GAIN_ENERGY_CARBON'),
      assetState = this.wizardService.getState('ASSET_SELECTION');

    let initState: KPI.RoadmapIndicator[] = [];

    this.roadmapService.roadmapIndicators().subscribe(rIndicators => {
      if (rIndicators.length === 0) {
        this.wizardService.skipCurrentStep();
        return;
      }

      this.wizardService.unblockCurrentStep();

      // check previous state, if the energy/carbon gain is enabled or not
      // then check the impact accordingly
      if (energyState?.enabled) {
        initState = rIndicators.filter(ri => ri.is_energy_carbon);
      } else {
        currentState = currentState?.filter(ri => !ri.is_energy_carbon) || [];
      }
      // same logic for technical state
      if (!!assetState) {
        initState = [...initState, ...rIndicators.filter(ri => ri.technical_state_related)];
      } else {
        currentState = currentState?.filter(ri => !ri.technical_state_related)|| [];
      }
      const consolidateState = [...new Set([...(currentState || []), ...initState])]; // remove duplicates

      this.wizardService.setState(this.stateKey, consolidateState);
      this.impacts$ = new BehaviorSubject<KPI.RoadmapIndicator[]>( consolidateState || []);
    });
  }

  checkedChange(checked: boolean, roadmapIndicator: KPI.RoadmapIndicator) {
    let updated: KPI.RoadmapIndicator[];
    if (checked) {
      // add it
      updated = [...this.impacts$.value, roadmapIndicator];
    } else {
      // remove it
      updated = this.impacts$.value.filter(i => i.id !== roadmapIndicator.id);
    }
    this.impacts$.next(updated);
    this.wizardService.setState(this.stateKey, updated);
  }

  selected(id: number): boolean {
    return this.impacts$.value.map(i => i.id).includes(id);
  }
}
